<template>
  <el-form-item 
    class="sm-text-field w-100" 
    :label="label" 
    label-position="top" 
    :prop="prop"
    :rules="rules"
  >
    <el-input
      ref="inputRef"
      :disabled="disabled"
      v-model="modelValueProxy"
      size="large"
      :clearable="clearable"
      :placeholder="placeholder"
      :type="inputType"
      :show-word-limit="showWordLimit"
      :maxlength="maxlength"
      :input-style="inputStyle"
      @blur="handleBlur"
      @change="handleChange"
      @keyup.enter="handleEnter"
    >
      <template #prefix v-if="icon">
        <i :class="['text-primary ti', `ti-${icon}`]"></i>
      </template>
      <template #suffix v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </template>
    </el-input>
  </el-form-item>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { FormItemRule, InputInstance } from 'element-plus'

interface Props {
  modelValue: string | number | null
  prop?: string
  placeholder?: string
  icon?: string
  disabled?: boolean
  clearable?: boolean
  label: string
  type?: 'text' | 'password' | 'number' | 'email' | 'url' | 'tel'
  rules?: FormItemRule | FormItemRule[]
  showWordLimit?: boolean
  maxlength?: number
  inputStyle?: Record<string, string> | string
}

const props = withDefaults(defineProps<Props>(), {
  prop: undefined,
  placeholder: '',
  icon: 'pencil',
  disabled: false,
  clearable: false,
  type: 'text',
  showWordLimit: false,
  maxlength: undefined,
  inputStyle: undefined,
  rules: undefined
})

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'change',
  'enter'
])

const inputRef = ref<InputInstance>()

const modelValueProxy = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const inputType = computed(() => {
  if (props.type === 'number') return 'text' // Use text type with number validation
  return props.type
})

const handleBlur = (event: Event) => {
  emit('blur', event)
}

const handleChange = (value: string | number) => {
  // Convert to number if type is number
  if (props.type === 'number' && value !== null) {
    const numValue = Number(value)
    if (!isNaN(numValue)) {
      emit('update:modelValue', numValue)
    }
  }
  emit('change', value)
}

const handleEnter = () => {
  emit('enter')
}

// Expose focus method for parent components
const focus = () => {
  inputRef.value?.focus()
}

defineExpose({
  focus
})
</script>

<style scoped>
.sm-text-field {
  margin-bottom: 20px;
}

.sm-text-field :deep(.el-form-item__label) {
  font-weight: 500;
  margin-bottom: 8px;
}
</style>